import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Clock,
  GraduationCap,
  Shield,
  Users,
  Heart,
  Building2,
  Briefcase,
  CheckCircle,
  ArrowRight,
  Send,
  FileText,
  Lightbulb,
  TrendingUp,
  MessageSquare,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const comingSoonPolicies = [
  {
    icon: GraduationCap,
    title: "Education & Youth Opportunity",
    desc: "Strengthening public schools in high-need areas, expanding early childhood education access, investing in career and technical education (CTE), and building after-school and youth development programs across Nevada.",
    color: "bg-blue-600",
  },
  {
    icon: Shield,
    title: "Public Safety & Community Investment",
    desc: "Developing violence prevention and intervention programs, community-based safety initiatives, youth engagement and mentorship pathways, and infrastructure revitalization for underserved neighborhoods.",
    color: "bg-emerald-600",
  },
  {
    icon: Users,
    title: "Equity & Opportunity Across Communities",
    desc: "Addressing systemic barriers affecting underserved communities, improving access to opportunity in housing, education, and employment systems, strengthening civil rights protections in practice, and ensuring fair access to state programs and services.",
    color: "bg-violet-600",
  },
  {
    icon: Heart,
    title: "Support for Women & Family Stability",
    desc: "Expanding childcare accessibility and affordability, coordinating maternal and family health support, enforcing workplace fairness and equal opportunity, and reducing economic instability for Nevada families.",
    color: "bg-rose-600",
  },
  {
    icon: Building2,
    title: "Support for Black-Owned & Minority-Owned Businesses",
    desc: "Improving access to capital and financing, expanding participation in state contracts and procurement, building business mentorship and incubation programs, and reducing administrative barriers for new entrepreneurs.",
    color: "bg-amber-600",
  },
  {
    icon: Briefcase,
    title: "Young Adults & Future Workforce Development",
    desc: "Creating first-job pipelines and entry-level opportunities, aligning education with workforce needs, addressing affordability challenges facing young adults, and building civic and economic participation pathways.",
    color: "bg-cyan-600",
  },
];

const activePolicies = [
  {
    title: "Pro-Worker & Pro-Union Economic Framework",
    desc: "A comprehensive plan focused on fair growth — not anti-business, but pro-fairness — already in active development.",
    path: "/policies/pro-union",
    status: "Active",
  },
  {
    title: "eNVy Healthcare Reform",
    desc: "A Nevada-specific healthcare initiative designed to lower costs and expand access for working families.",
    path: "/policies/healthcare",
    status: "Active",
  },
  {
    title: "Housing & Cost-of-Living Stability",
    desc: "Proposals to lower housing costs and stabilize rental prices are already underway with detailed plans published.",
    path: "/policies/housing",
    status: "Active",
  },
];

const ComingSoonPage = () => {
  const [email, setEmail] = useState("");
  const [subStatus, setSubStatus] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [fbStatus, setFbStatus] = useState(null);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      const res = await fetch(`${API_URL}/api/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      setSubStatus(data.status === "subscribed" || data.status === "already_subscribed" ? "success" : "error");
      if (data.status === "subscribed") setEmail("");
    } catch {
      setSubStatus("error");
    }
  };

  const handleFeedback = async (e) => {
    e.preventDefault();
    if (!feedback.trim()) return;
    try {
      await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: "Policy Feedback",
          email: "feedback@cooperfornv.com",
          subject: "Policy Platform Feedback",
          message: feedback,
        }),
      });
      setFbStatus("success");
      setFeedback("");
    } catch {
      setFbStatus("error");
    }
  };

  return (
    <div data-testid="coming-soon-page" className="pt-16 md:pt-20">
      {/* ── HERO ── */}
      <section className="relative py-16 md:py-28 bg-[#002B7F]">
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23D4AF37\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            }}
          />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-[0.2em] mb-6">
            <FileText className="w-4 h-4" />
            Policy Platform
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-6">
            Policy Platform in Development
          </h1>
          <p className="text-lg text-[#C0C0C0] max-w-2xl mx-auto mb-6">
            This platform is being built in collaboration with Nevadans,
            workers, families, and community leaders. Several major initiatives
            are already in progress, with more policies being developed and
            released soon.
          </p>
          <p className="text-white/60 max-w-xl mx-auto">
            Real governance requires practical solutions, not slogans. Each
            policy area is being developed with community input, data-driven
            analysis, and a phased rollout designed for real-world impact.
          </p>
        </div>
      </section>

      {/* ── ACTIVE POLICIES ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <TrendingUp className="w-6 h-6 text-emerald-600" />
            <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F]">
              Active Policy Initiatives Already in Development
            </h2>
          </div>

          <div className="space-y-4">
            {activePolicies.map((policy) => (
              <Link
                key={policy.title}
                to={policy.path}
                className="block bg-[#002B7F]/5 border-2 border-[#002B7F]/10 p-5 hover:border-[#D4AF37] transition-all group"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-bold text-[#002B7F]">
                        {policy.title}
                      </h3>
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-wider">
                        {policy.status}
                      </span>
                    </div>
                    <p className="text-slate-600 text-sm">{policy.desc}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-[#D4AF37] transition-colors flex-shrink-0 mt-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMING SOON POLICIES ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Clock className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-4">
              Policy Areas in Development
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              These policy areas are currently being developed with input from
              community leaders, subject matter experts, and Nevada residents.
              Each will be published in full detail as it is finalized.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {comingSoonPolicies.map((policy) => {
              const Icon = policy.icon;
              return (
                <div
                  key={policy.title}
                  className="bg-white border-2 border-[#002B7F]/10 p-6"
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-10 h-10 flex-shrink-0 flex items-center justify-center ${policy.color} text-white`}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-bold text-[#002B7F] text-sm">
                          {policy.title}
                        </h3>
                      </div>
                      <p className="text-slate-600 text-xs leading-relaxed mb-3">
                        {policy.desc}
                      </p>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#D4AF37]/15 border border-[#D4AF37]/30 text-[#002B7F] text-xs font-bold uppercase tracking-wider">
                        <Clock className="w-3 h-3" />
                        Coming Soon
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── GET UPDATES ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Send className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Get Updates as Policies Are Released
          </h2>
          <p className="text-[#C0C0C0] mb-8 max-w-xl mx-auto">
            Be the first to know when new policies are published. Sign up for
            campaign updates.
          </p>

          <form
            onSubmit={handleSubscribe}
            className="flex gap-2 max-w-md mx-auto mb-4"
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              data-testid="coming-soon-email"
              className="flex-1 px-4 py-3 bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:border-[#D4AF37]"
            />
            <button
              type="submit"
              data-testid="coming-soon-subscribe"
              className="px-6 py-3 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all"
            >
              Subscribe
            </button>
          </form>
          {subStatus === "success" && (
            <p className="text-emerald-400 text-sm">
              You're subscribed! We'll notify you as new policies are released.
            </p>
          )}
          {subStatus === "error" && (
            <p className="text-red-400 text-sm">
              Something went wrong. Please try again.
            </p>
          )}
        </div>
      </section>

      {/* ── HELP SHAPE THIS PLATFORM ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <Lightbulb className="w-10 h-10 text-[#002B7F] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-4">
              Help Shape This Platform
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto">
              This platform belongs to Nevada. If there's a policy area you
              think we should prioritize, or an issue you want addressed, let
              us know.
            </p>
          </div>

          <form onSubmit={handleFeedback} className="max-w-lg mx-auto">
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="What issues matter most to you? What should this platform address?"
              rows={4}
              data-testid="feedback-textarea"
              className="w-full px-4 py-3 border-2 border-[#002B7F]/10 text-sm focus:outline-none focus:border-[#D4AF37] resize-none mb-4"
            />
            <div className="text-center">
              <button
                type="submit"
                data-testid="feedback-submit"
                className="inline-flex items-center gap-2 px-8 py-3 bg-[#002B7F] hover:bg-[#001F5C] text-white font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
              >
                <MessageSquare className="w-4 h-4" />
                Submit Feedback
              </button>
            </div>
            {fbStatus === "success" && (
              <p className="text-emerald-600 text-sm text-center mt-3">
                Thank you for your input. Your feedback has been received.
              </p>
            )}
            {fbStatus === "error" && (
              <p className="text-red-600 text-sm text-center mt-3">
                Something went wrong. Please try again.
              </p>
            )}
          </form>
        </div>
      </section>

      {/* ── CLOSING ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg text-[#002B7F] font-bold leading-relaxed mb-4">
            This platform is under active development. As each policy is
            finalized, it will be published here in full detail.
          </p>
          <p className="text-slate-600">
            Building a better Nevada — one policy at a time.
          </p>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Explore the Policies Already Published
          </h2>
          <p className="text-[#C0C0C0] mb-8">
            Several major policy initiatives are already available with full
            details.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/policies/housing"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              View All Policies
            </Link>
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Get Involved
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/40 text-white/80 hover:border-white hover:text-white font-bold uppercase tracking-widest text-sm transition-all"
            >
              Contact the Campaign
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ComingSoonPage;
